'use es6';

import PropTypes from 'prop-types';
import { memo } from 'react';
import { useDrop } from 'react-dnd';
import { isEmpty } from 'underscore';
import DashedDropZone from './DashedDropZone';
import { isFieldContactProperty } from 'FormsUI/utils/DragAndDrop';
import { DragTypes } from 'FormsUI/constants/DragAndDrop';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { DRAG_DROP_REORDERED_FORM_FIELD_TO_NEW_GROUP, DRAG_DROP_ADDED_FORM_FIELD_FROM_PANEL_TO_NEW_GROUP, DRAG_DROP_REORDERED_RICH_TEXT } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { jsx as _jsx } from "react/jsx-runtime";
const FormFieldGroupDropTarget = /*#__PURE__*/memo(({
  formFieldGroupIndex,
  isQueued = false,
  isLastRow,
  hasRichText,
  addFormFieldGroup,
  moveFormFieldIntoNewFormFieldGroup,
  reorderRichText
}) => {
  const [{
    hasDragOver,
    isDragSourceDroppable
  }, drop] = useDrop({
    accept: [DragTypes.NEW_FORM_FIELD, DragTypes.REORDER_FORM_FIELD, DragTypes.REORDER_RICH_TEXT],
    canDrop: (item, monitor) => {
      const draggedItemType = monitor.getItemType();
      const draggedItem = item;
      const sourceFormFieldGroupIndex = draggedItem.formFieldGroupIndex;
      const targetFormFieldGroupIndex = formFieldGroupIndex;
      const isRichTextField = !isEmpty(draggedItem.richTextContent);
      const numberFormFieldsWithinGroup = draggedItem.numberFormFieldsWithinGroup;
      const isSourceQueued = draggedItem.isSourceQueued;
      const isPageBreak = draggedItem.isPageBreak;
      const isTargetQueued = isQueued;
      if (isPageBreak) {
        return targetFormFieldGroupIndex > 0;
      }
      if (isTargetQueued) {
        if (!isFieldContactProperty(draggedItem)) {
          return false;
        }
      }
      if (isTargetQueued && isRichTextField) {
        return !isLastRow && sourceFormFieldGroupIndex !== targetFormFieldGroupIndex && !hasRichText;
      }
      if (draggedItemType === DragTypes.NEW_FORM_FIELD) {
        return true;
      }
      return numberFormFieldsWithinGroup > 1 && draggedItemType !== DragTypes.REORDER_RICH_TEXT || sourceFormFieldGroupIndex !== targetFormFieldGroupIndex && sourceFormFieldGroupIndex + 1 !== targetFormFieldGroupIndex || isSourceQueued !== isTargetQueued;
    },
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return;
      }
      const draggedItemType = monitor.getItemType();
      const draggedItem = item;
      const targetFormFieldGroupIndex = formFieldGroupIndex;
      if (draggedItemType === DragTypes.NEW_FORM_FIELD) {
        const propertyId = draggedItem.propertyId;
        addFormFieldGroup(propertyId, targetFormFieldGroupIndex, {
          isQueued,
          newFieldType: draggedItem.fieldType,
          richTextContent: draggedItem.richTextContent,
          isPageBreak: draggedItem.isPageBreak
        });
        trackEditorInteraction(DRAG_DROP_ADDED_FORM_FIELD_FROM_PANEL_TO_NEW_GROUP);
      } else if (draggedItemType === DragTypes.REORDER_FORM_FIELD) {
        const sourceFormFieldGroupIndex = draggedItem.formFieldGroupIndex;
        const formFieldName = draggedItem.formFieldName;
        const formFieldPropertyObjectType = draggedItem.formFieldPropertyObjectType;
        moveFormFieldIntoNewFormFieldGroup(sourceFormFieldGroupIndex, formFieldName, formFieldPropertyObjectType, targetFormFieldGroupIndex, {
          isQueued
        });
        trackEditorInteraction(DRAG_DROP_REORDERED_FORM_FIELD_TO_NEW_GROUP);
      } else if (draggedItemType === DragTypes.REORDER_RICH_TEXT) {
        const sourceFormFieldGroupIndex = draggedItem.formFieldGroupIndex;
        reorderRichText(sourceFormFieldGroupIndex, targetFormFieldGroupIndex, {
          isQueued
        });
        trackEditorInteraction(DRAG_DROP_REORDERED_RICH_TEXT);
      }
    },
    collect: monitor => ({
      hasDragOver: monitor.isOver(),
      isDragSourceDroppable: monitor.canDrop()
    })
  });
  if (!isDragSourceDroppable) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    ref: drop,
    children: /*#__PURE__*/_jsx(DashedDropZone, {
      isHover: hasDragOver,
      isVertical: false
    })
  });
});
FormFieldGroupDropTarget.propTypes = {
  formFieldGroupIndex: PropTypes.number.isRequired,
  isQueued: PropTypes.bool,
  isLastRow: PropTypes.bool,
  hasRichText: PropTypes.bool,
  addFormFieldGroup: PropTypes.func.isRequired,
  moveFormFieldIntoNewFormFieldGroup: PropTypes.func.isRequired,
  reorderRichText: PropTypes.func.isRequired
};
export default FormFieldGroupDropTarget;